<template>
  <hel-module title="订单与产能对比" :class="scope.clsPrefix">
    <div class="chart-container">
      <div class="chart-header">
        <div class="total-demand">
          <span class="demand-label">1-{{ months.length }}月累计需求</span>
          <span class="demand-value">{{ totalDemand }}</span>
          <span class="demand-unit">{{ unit }}</span>
        </div>
        <div class="legend-area">
          <span class="legend-title">订单需求与产能趋势</span>
          <div class="legend-list">
            <span class="legend-item">
              <i class="legend-line demand-line"></i>
              订单需求
            </span>
            <span class="legend-item">
              <i class="legend-line capacity-line"></i>
              产能
            </span>
          </div>
        </div>
      </div>
      <div id="orderCapacityChart" class="chart-body"></div>
    </div>
  </hel-module>
</template>

<script>
import HelModule from './common/module/index.vue'
import * as echarts from 'echarts'
import axios from 'axios'

export default {
  name: 'OrderCapacityComparison',
  components: {
    HelModule
  },
  props: {
    data: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  data() {
    return {
      scope: {
        clsPrefix: 'rvc-cosmoplat-workspace-order-capacity'
      },
      months: [],
      demandData: [],
      capacityData: [],
      unit: '',
      chartInstance: null
    }
  },
  computed: {
    totalDemand() {
      return this.demandData
        .reduce((sum, val) => sum + val, 0)
        .toLocaleString('en-US')
    }
  },
  mounted() {
    this.fetchOrderCapacity()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
  },
  methods: {
    async fetchOrderCapacity() {
      try {
        const response = await axios.post(
          'https://bigdata-cloud.cosmoplat.com/prod-api/system/api/call/erp/ordersAndProductionCapacity',
          {
            secret: '9k0NVP'
          },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        )

        console.log('订单与产能对比接口返回:', response.data)

        if (
          response.data.code === 200 &&
          Array.isArray(response.data.data) &&
          response.data.data.length > 0
        ) {
          // 按统计月份升序排列
          const sortedList = [...response.data.data].sort(
            (a, b) => Number(a.statMonth) - Number(b.statMonth)
          )

          this.months = sortedList.map((item) => `${item.statMonth}月`)
          this.demandData = sortedList.map((item) => Number(item.orderDemand))
          this.capacityData = sortedList.map((item) =>
            Number(item.productionQty)
          )
          this.unit = sortedList[0].unit || ''

          this.$nextTick(() => {
            this.drawChart()
          })
        }
      } catch (error) {
        console.error('获取订单与产能对比数据失败:', error)
      }
    },
    drawChart() {
      const chart = echarts.init(
        document.getElementById('orderCapacityChart')
      )
      const unit = this.unit
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'line'
          },
          valueFormatter: (val) =>
            Number(val).toLocaleString('en-US') + (unit ? ' ' + unit : '')
        },
        grid: {
          top: 10,
          left: 10,
          right: 20,
          bottom: 10,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: this.months,
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: '#999999',
            fontSize: 12
          }
        },
        yAxis: {
          type: 'value',
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          splitLine: {
            lineStyle: {
              color: '#f0f0f0',
              type: 'dashed'
            }
          },
          axisLabel: {
            color: '#999999',
            fontSize: 12,
            formatter: (val) =>
              val.toLocaleString('en-US') + (unit ? ' ' + unit : '')
          }
        },
        series: [
          {
            name: '订单需求',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            data: this.demandData,
            itemStyle: {
              color: '#4A64F4'
            },
            lineStyle: {
              color: '#4A64F4',
              width: 2
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(74, 100, 244, 0.15)' },
                { offset: 1, color: 'rgba(74, 100, 244, 0.02)' }
              ])
            }
          },
          {
            name: '产能',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            data: this.capacityData,
            itemStyle: {
              color: '#999999'
            },
            lineStyle: {
              color: '#999999',
              width: 2,
              type: 'dashed'
            }
          }
        ]
      }
      chart.setOption(option)
      this.chartInstance = chart
      // 修复 flex 容器下初始化时高度为 0 导致图表不回显的问题
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.chartInstance && this.chartInstance.resize()
        })
        setTimeout(() => {
          this.chartInstance && this.chartInstance.resize()
        }, 200)
      })
    },
    handleResize() {
      if (this.chartInstance) {
        this.chartInstance.resize()
      }
    }
  }
}
</script>

<style lang="scss">
$class-prefix: "rvc-cosmoplat-workspace-order-capacity";
.#{$class-prefix} {
  .chart-container {
    height: 100%;
    min-height: 280px;
    display: flex;
    flex-direction: column;
    padding: 10px 0;
    box-sizing: border-box;
  }
  .chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }
  .total-demand {
    display: flex;
    align-items: baseline;
    gap: 4px;
    .demand-label {
      font-family: PingFangSC-Regular;
      font-size: 12px;
      color: #666666;
      font-weight: 400;
    }
    .demand-value {
      font-family: PingFangSC-Medium;
      font-size: 18px;
      color: #333333;
      font-weight: 600;
    }
    .demand-unit {
      font-family: PingFangSC-Regular;
      font-size: 12px;
      color: #666666;
      font-weight: 400;
    }
  }
  .legend-area {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    .legend-title {
      font-family: PingFangSC-Regular;
      font-size: 12px;
      color: #999999;
      font-weight: 400;
      margin-bottom: 4px;
    }
    .legend-list {
      display: flex;
      gap: 16px;
    }
    .legend-item {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-family: PingFangSC-Regular;
      font-size: 12px;
      color: #666666;
      font-weight: 400;
    }
    .legend-line {
      display: inline-block;
      width: 16px;
      height: 2px;
      border-radius: 1px;
      &.demand-line {
        background: #4a64f4;
      }
      &.capacity-line {
        background: #999999;
        background-image: repeating-linear-gradient(
          to right,
          #999999 0,
          #999999 4px,
          transparent 4px,
          transparent 8px
        );
      }
    }
  }
  .chart-body {
    flex: 1;
    width: 100%;
    height: 100%;
    min-height: 220px;
  }
}
</style>
